
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface EmailDialogProps {
  open: boolean;
  onClose: () => void;
  onSend: (email: string) => void;
  defaultEmail?: string;
  defaultRecipients?: string[];
  isSending?: boolean;
}

export const EmailDialog = ({ open, onClose, onSend, defaultEmail = '', defaultRecipients = [], isSending = false }: EmailDialogProps) => {
  const [email, setEmail] = useState(defaultEmail);
  const [recipientOptions, setRecipientOptions] = useState<string[]>([]);

  useEffect(() => {
    if (defaultRecipients.length > 0) {
      setRecipientOptions(defaultRecipients);
    }
    
    if (defaultEmail) {
      setEmail(defaultEmail);
    }
  }, [defaultEmail, defaultRecipients]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSend(email);
    onClose();
  };

  const selectRecipient = (recipient: string) => {
    setEmail(recipient);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-center text-xl">Send Expense Report</DialogTitle>
          <DialogDescription className="text-center">
            Enter the recipient's email address to send the expense report as a PDF.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {recipientOptions.length > 0 && (
              <div className="grid gap-2">
                <Label>Default Recipients</Label>
                <div className="flex flex-wrap gap-2">
                  {recipientOptions.map((recipient, index) => (
                    <Button
                      key={index}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => selectRecipient(recipient)}
                      className={cn(
                        "rounded-xl", 
                        email === recipient ? "bg-primary/10 border-primary" : ""
                      )}
                    >
                      {recipient}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email" className="text-right">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="col-span-3 rounded-xl"
                required
              />
            </div>
          </div>
          <DialogFooter className="flex-col space-y-2 sm:space-y-0">
            <Button type="submit" disabled={isSending} className="w-full sm:w-auto rounded-xl">
              {isSending ? "Sending..." : "Send Email"}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSending} className="w-full sm:w-auto rounded-xl">
              Cancel
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Helper function to conditionally join classNames
const cn = (...classes: Array<string | boolean | undefined>): string => {
  return classes.filter(Boolean).join(' ');
};
